import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  TreeSnapshot,
  YearTreeData,
  ProjectTreeData,
  PulseTreeData,
} from '@shared/projectTreeDto';
import { refreshAuthToken, clearAuth } from '@/lib/auth';
import { deltaManager } from '@/services/delta-manager';

/**
 * Options for useProjectTreeData hook
 */
export interface UseProjectTreeDataOptions {
  yearId?: string;
  includePulses?: boolean;
  enabled?: boolean;
}

/**
 * Unified hook for fetching and accessing project tree data from the aggregated API
 * 
 * Features:
 * - Fetches complete tree snapshot from /api/tree/snapshot
 * - Supports filtering by year
 * - Optional pulse inclusion for performance
 * - Works seamlessly with WebSocket cache updates
 * - Returns flattened accessors for easy data access
 * 
 * IMPORTANT - Query Key Stability:
 * This hook uses a STABLE query key pattern to ensure proper caching and WebSocket updates.
 * 
 * ❌ BROKEN (causes cache misses):
 *   queryKey: ['/api/tree/snapshot', options]
 *   // 'options' is a new object reference on each render
 * 
 * ✅ FIXED (stable caching):
 *   queryKey: ['/api/tree/snapshot', { yearId: options?.yearId, includePulses: options?.includePulses ?? false }]
 *   // Object with primitive values creates stable hash
 * 
 * WARNING: When calling this hook, DO NOT pass inline object literals:
 *   ❌ useProjectTreeData({ yearId: '2025' })  // New object each render!
 *   ✅ const options = useMemo(() => ({ yearId: '2025' }), [yearId])
 *      useProjectTreeData(options)
 * 
 * WebSocket Compatibility:
 * The stable key pattern works with WebSocket predicate queries:
 *   queryClient.setQueriesData({ queryKey: ['/api/tree/snapshot'], exact: false }, ...)
 * This matches ALL tree queries regardless of yearId/includePulses options.
 * 
 * @param options - Configuration options
 * @returns Tree snapshot data with convenient accessors
 */
export function useProjectTreeData(options?: UseProjectTreeDataOptions) {
  const {
    yearId,
    includePulses = false,
    enabled = true,
  } = options || {};

  // Build query params for the API call
  const queryParams = new URLSearchParams();
  if (yearId) {
    queryParams.append('yearId', yearId);
  }
  if (includePulses) {
    queryParams.append('includePulses', 'true');
  }

  // Construct the full query URL
  const queryUrl = `/api/tree/snapshot${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  // Fetch tree snapshot with React Query
  // CRITICAL: Use stable query key with primitive values, NOT the options object reference
  // This ensures cache hits on re-renders and WebSocket updates propagate correctly
  // NOTE: Server now returns { snapshot: TreeSnapshot, sequenceNumber: number }
  const query = useQuery<{ snapshot: TreeSnapshot; sequenceNumber: number }>({
    queryKey: ['/api/tree/snapshot', { 
      yearId: options?.yearId, 
      includePulses: options?.includePulses ?? false 
    }],
    queryFn: async () => {
      const startTime = performance.now();
      console.log('[ProjectTree Client] Fetching tree snapshot...');
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      let response = await fetch(queryUrl, {
        headers,
        credentials: 'include',
      });
      
      // Handle 401 - try token refresh, then redirect to login if still failing
      if (response.status === 401) {
        console.log('[ProjectTree Client] Token expired, attempting refresh...');
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          response = await fetch(queryUrl, {
            headers,
            credentials: 'include',
          });
        } else {
          // Refresh failed - clear auth and redirect to login
          console.log('[ProjectTree Client] Token refresh failed, redirecting to login');
          clearAuth();
          window.location.href = '/login';
          throw new Error('Session expired - redirecting to login');
        }
      }
      
      const fetchDuration = performance.now() - startTime;
      console.log(`[ProjectTree Client] Fetch completed in ${fetchDuration.toFixed(0)}ms`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tree snapshot: ${response.statusText}`);
      }
      
      const data = await response.json();
      const yearsCount = data.snapshot?.years?.length || 0;
      const projectsCount = data.snapshot?.years?.reduce((sum: number, y: any) => sum + (y.projects?.length || 0), 0) || 0;
      console.log(`[ProjectTree Client] *** RECEIVED *** ${yearsCount} years with ${projectsCount} projects`);
      return data;
    },
    enabled,
    staleTime: 2000, // 2 seconds - prevents event storms while allowing real-time feel
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
    refetchOnReconnect: true, // Re-sync sequence number after network reconnection
    placeholderData: (prev) => prev, // KEY: Show old data while fetching - eliminates flicker
    notifyOnChangeProps: ['data', 'error'], // Prevent re-renders on isFetching changes
  });

  // Bootstrap deltaManager with initial sequence number from server
  useEffect(() => {
    if (query.data?.sequenceNumber !== undefined) {
      if (!deltaManager.isBootstrapped()) {
        deltaManager.bootstrap(query.data.sequenceNumber);
        console.log('[useProjectTreeData] Bootstrapped DeltaManager with sequence:', query.data.sequenceNumber);
      } else {
        // Refetched snapshot (e.g. after an invalidation): advance the
        // delta cursor monotonically so live deltas aren't misread as
        // gaps/duplicates against a stale cursor.
        deltaManager.reconcileSnapshotSequence(query.data.sequenceNumber);
      }
    }
  }, [query.data?.sequenceNumber]);

  // Derive convenient accessor properties from unwrapped snapshot
  const snapshot = query.data?.snapshot;
  const years = snapshot?.years || [];
  const projects = years.flatMap(year => year.projects);

  return {
    snapshot,
    years,
    projects,
    sequenceNumber: query.data?.sequenceNumber,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to get a specific project by ID from the tree cache
 * 
 * This hook reads from the cached tree snapshot without making additional API calls.
 * It will return undefined if the tree data is not yet loaded or the project is not found.
 * 
 * @param projectId - The project ID to find
 * @returns The project data or undefined
 */
export function useProjectById(projectId: string): ProjectTreeData | undefined {
  const queryClient = useQueryClient();

  // Try to find the project in any cached tree snapshot
  const wrappedSnapshots = queryClient.getQueriesData<{ snapshot: TreeSnapshot; sequenceNumber: number }>({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return Array.isArray(queryKey) && queryKey[0] === '/api/tree/snapshot';
    },
  });

  // Search through all cached snapshots (unwrap the snapshot from the wrapper)
  for (const [_, wrappedData] of wrappedSnapshots) {
    if (!wrappedData?.snapshot) continue;
    const snapshot = wrappedData.snapshot;

    // Check projectMap first for O(1) lookup
    if (snapshot.projectMap && snapshot.projectMap[projectId]) {
      return snapshot.projectMap[projectId];
    }

    // Fallback to searching through years
    for (const year of snapshot.years) {
      const project = year.projects.find(p => p.id === projectId);
      if (project) {
        return project;
      }
    }
  }

  return undefined;
}

/**
 * Hook to get pulses for a specific project from the tree cache
 * 
 * This hook reads from the cached tree snapshot without making additional API calls.
 * Returns an empty array if the tree data is not yet loaded, the project is not found,
 * or pulses were not included in the tree snapshot.
 * 
 * @param projectId - The project ID to get pulses for
 * @returns Array of pulses for the project
 */
export function usePulsesByProject(projectId: string): PulseTreeData[] {
  const project = useProjectById(projectId);
  return project?.pulses || [];
}

/**
 * Hook to get a specific pulse by ID from the tree cache
 * 
 * This hook reads from the cached tree snapshot without making additional API calls.
 * It will return undefined if the tree data is not yet loaded or the pulse is not found.
 * 
 * @param pulseId - The pulse ID to find
 * @returns The pulse data or undefined
 */
export function usePulseById(pulseId: number): PulseTreeData | undefined {
  const queryClient = useQueryClient();

  // Try to find the pulse in any cached tree snapshot
  const wrappedSnapshots = queryClient.getQueriesData<{ snapshot: TreeSnapshot; sequenceNumber: number }>({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return Array.isArray(queryKey) && queryKey[0] === '/api/tree/snapshot';
    },
  });

  // Search through all cached snapshots (unwrap the snapshot from the wrapper)
  for (const [_, wrappedData] of wrappedSnapshots) {
    if (!wrappedData?.snapshot) continue;
    const snapshot = wrappedData.snapshot;

    // Check pulseMap first for O(1) lookup
    if (snapshot.pulseMap && snapshot.pulseMap[pulseId]) {
      return snapshot.pulseMap[pulseId];
    }

    // Fallback to searching through years and projects
    for (const year of snapshot.years) {
      for (const project of year.projects) {
        if (project.pulses) {
          const pulse = project.pulses.find(p => p.id === pulseId);
          if (pulse) {
            return pulse;
          }
        }
      }
    }
  }

  return undefined;
}

/**
 * Hook to get all projects for a specific year from the tree cache
 * 
 * This hook reads from the cached tree snapshot without making additional API calls.
 * Returns an empty array if the tree data is not yet loaded or the year is not found.
 * 
 * @param yearId - The year ID to get projects for
 * @returns Array of projects for the year
 */
export function useProjectsByYear(yearId: string): ProjectTreeData[] {
  const queryClient = useQueryClient();

  // Try to find the year in any cached tree snapshot
  const wrappedSnapshots = queryClient.getQueriesData<{ snapshot: TreeSnapshot; sequenceNumber: number }>({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return Array.isArray(queryKey) && queryKey[0] === '/api/tree/snapshot';
    },
  });

  // Search through all cached snapshots (unwrap the snapshot from the wrapper)
  for (const [_, wrappedData] of wrappedSnapshots) {
    if (!wrappedData?.snapshot) continue;
    const snapshot = wrappedData.snapshot;

    const year = snapshot.years.find(y => y.id === yearId);
    if (year) {
      return year.projects;
    }
  }

  return [];
}

/**
 * Hook to get the active year from the tree cache
 * 
 * This hook reads from the cached tree snapshot without making additional API calls.
 * Returns undefined if the tree data is not yet loaded or no active year is found.
 * 
 * @returns The active year data or undefined
 */
export function useActiveYear(): YearTreeData | undefined {
  const queryClient = useQueryClient();

  // Try to find the active year in any cached tree snapshot
  const wrappedSnapshots = queryClient.getQueriesData<{ snapshot: TreeSnapshot; sequenceNumber: number }>({
    predicate: (query) => {
      const queryKey = query.queryKey;
      return Array.isArray(queryKey) && queryKey[0] === '/api/tree/snapshot';
    },
  });

  // Search through all cached snapshots (unwrap the snapshot from the wrapper)
  for (const [_, wrappedData] of wrappedSnapshots) {
    if (!wrappedData?.snapshot) continue;
    const snapshot = wrappedData.snapshot;

    const activeYear = snapshot.years.find(y => y.isActive);
    if (activeYear) {
      return activeYear;
    }
  }

  return undefined;
}
